import React from "react";

import { useGetFarmDocScanField } from "utils";

import { Alert, Button, FieldBoundaryPreview, Icon, SkeletonWrapper, TagNormal, Typography } from "components";

import { DocScanComparisonTable } from "pages/admin/verifications/components/automations/DocScanComparisonTable";
import { DocScanFieldMatch } from "pages/admin/verifications/components/automations/DocScanFieldMatch";
import { DocScanReview } from "pages/admin/verifications/components/automations/DocScanReview";
import { DocScanSourceDocument } from "pages/admin/verifications/components/automations/DocScanSourceDocument";
import { getDocScanEvidenceTitle, getDocScanEvidenceTone } from "pages/admin/verifications/utils/docScanEvidence";
import { getDocScanBoundaryShapes, getDocScanComparisonRows } from "pages/admin/verifications/utils/docScanEvidenceDrawer";

const BADGE_COLORS = { success: "green", danger: "red", warning: "orange" } as const;

export const DocScanEvidenceTab = ({ farmId, scanFieldId }: { farmId: string; scanFieldId: string }) => {
  const { data: evidence, isLoading, isError, refetch } = useGetFarmDocScanField({ farmId, scanFieldId });
  const boundaryShapes = evidence ? getDocScanBoundaryShapes(evidence) : null;

  if (isError) {
    return (
      <Alert
        className={undefined}
        closable={false}
        type="red"
        headline="The document evidence didn't load"
        description={
          <Button type="secondary" size="small" onClick={() => refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  return (
    <SkeletonWrapper isLoading={isLoading || !evidence} type="paragraph" rows={10}>
      {evidence && (
        <>
          <div className="doc-scan-evidence-header">
            <Icon iconName="tabler/file-check" size="m" />
            <Typography variant="h5">Document · main crop</Typography>
            <TagNormal
              className="doc-scan-evidence-badge"
              variant="paragraph-s"
              color={BADGE_COLORS[getDocScanEvidenceTone(evidence)]}
              text={getDocScanEvidenceTitle(evidence)}
            />
          </div>
          <DocScanFieldMatch evidence={evidence} />
          <DocScanComparisonTable rows={getDocScanComparisonRows(evidence)} />
          {boundaryShapes && (
            <FieldBoundaryPreview
              platformShape={boundaryShapes.platformShape}
              documentShapes={boundaryShapes.documentShapes}
              title="Field boundary comparison"
              platformLabel="Platform field"
              documentLabel="CAP parcel"
              expandLabel="Expand map"
              closeLabel="Close"
            />
          )}
          <DocScanSourceDocument fileName={evidence.fileName} fileUrl={evidence.fileUrl} sourceLocations={evidence.sourceLocations} />
          <DocScanReview farmId={farmId} scanFieldId={scanFieldId} scanId={evidence.scanId} review={evidence.review} />
        </>
      )}
    </SkeletonWrapper>
  );
};
